import React, { useState } from 'react'
import { Button, Modal } from 'react-bootstrap'

import useTranslation from '../../Hooks/UseTranslation'

import TasksImporter from '../TasksImporter/TasksImporter'
import TasksImporterV2 from '../TasksImporterV2/TasksImporterV2'
import DragAndDropArea from './DragAnDropArea'
import { detectFormat, DetectedFormat } from './detectFormat'

type Props = {
  show: boolean
  projectId: string
  onHide: () => void
  onImported: () => void
}

const TasksImporterUnified = (props: Props) => {

  const { t } = useTranslation()

  const [ fileBuffer, setFileBuffer ] = useState<ArrayBuffer | null>(null)
  const [ format, setFormat ] = useState<DetectedFormat | null>(null)
  const [ errorKey, setErrorKey ] = useState<string | null>(null)
  const [ detecting, setDetecting ] = useState(false)

  const reset = () => {

    setFileBuffer(null)
    setFormat(null)
    setErrorKey(null)
    setDetecting(false)
  }

  const handleHide = () => {

    reset()
    props.onHide()
  }

  const handleImported = () => {

    reset()
    props.onImported()
  }

  const handleFileSelected = async (file: File) => {

    reset()

    if (!file.name.toLowerCase().endsWith('.xlsx')) {

      setErrorKey('TASK_IMPORTER:InvalidXlsx')
      return
    }

    setDetecting(true)

    try {

      const buffer = await file.arrayBuffer()
      const detectedFormat = detectFormat(buffer)

      if (detectedFormat === 'unknown') {

        setErrorKey('TASK_IMPORTER:FormatUnknown')
        setFormat('unknown')
        return
      }

      setFileBuffer(buffer)
      setFormat(detectedFormat)
    } catch {

      setErrorKey('TASK_IMPORTER:InvalidXlsx')
      setFormat('unknown')
    } finally {

      setDetecting(false)
    }
  }

  if (!props.show) {

    return null
  }

  if (fileBuffer && format === 'pull-planning') {

    return (
      <TasksImporter
        show={props.show}
        projectId={props.projectId}
        fileBuffer={fileBuffer}
        onHide={handleHide}
        onImported={handleImported}
      />
    )
  }

  if (fileBuffer && format === 'gantt') {

    return (
      <TasksImporterV2
        show={props.show}
        projectId={props.projectId}
        fileBuffer={fileBuffer}
        onHide={handleHide}
        onImported={handleImported}
      />
    )
  }

  return (
    <Modal
      show={props.show}
      onHide={handleHide}
      backdrop='static'
      keyboard
    >
      <Modal.Header closeButton>
        <Modal.Title>{t('TASK_IMPORTER:Title')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {detecting && (
          <p className="text-center mb-3">
            {t('TASK_IMPORTER:DetectingFormat')}
          </p>
        )}
        {errorKey && (
          <div className="alert alert-danger" role="alert">
            {t(errorKey)}
          </div>
        )}
        <DragAndDropArea onFileSelected={handleFileSelected} />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleHide}>
          {t('TASK_IMPORTER:CancelButton')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default TasksImporterUnified
